import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { useState } from 'react';
import { InstagramShare } from 'capacitor-instagram-share-plugin';

interface ShareToStoryParams {
    backgroundVideoURL?: string;     // may be remote (https) or local (file://)
    stickerImageURL?: string;        // typically local file:// from the editor
    contentURL?: string;
    sourceApplication?: string;
}

// Outcome of a direct Instagram-Stories share, so the caller can toast on
// failure / fall back / only dismiss on success (instead of swallowing it).
export type InstagramShareResult = 'shared' | 'not-installed' | 'error';

// Download a remote URL into the app cache and return its file:// URI.
// Returns the input unchanged when it's already a local path.
async function ensureLocalFile(url: string, namePrefix: string): Promise<string> {
    if (!url) return url;
    if (url.startsWith('file://') || url.startsWith('/') || url.startsWith('capacitor://')) {
        return url;
    }
    if (!Capacitor.isNativePlatform()) {
        // On web, the IG share isn't reachable anyway; return as-is.
        return url;
    }

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    const blob = await res.blob();
    const base64 = await blobToBase64(blob);

    const ext = guessExtension(url, blob.type);
    const fileName = `${namePrefix}-${Date.now()}.${ext}`;
    const written = await Filesystem.writeFile({
        path: `ecliptic-share/${fileName}`,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
    });
    return written.uri;
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error || new Error('blobToBase64 failed'));
        reader.readAsDataURL(blob);
    });
}

function guessExtension(url: string, mime: string): string {
    const fromUrl = url.split('?')[0].split('#')[0].split('.').pop();
    if (fromUrl && fromUrl.length <= 5 && /^[a-z0-9]+$/i.test(fromUrl)) return fromUrl.toLowerCase();
    if (mime.includes('mp4')) return 'mp4';
    if (mime.includes('quicktime')) return 'mov';
    if (mime.includes('png')) return 'png';
    if (mime.includes('jpeg')) return 'jpg';
    return 'bin';
}

export const useInstagramShare = () => {
    const [instagramInstalled, setInstagramInstalled] = useState(false);
    const [loading, setLoading] = useState(false);

    const checkInstagram = async ({
        backgroundVideoURL,
        stickerImageURL,
        contentURL,
        sourceApplication,
    }: ShareToStoryParams): Promise<InstagramShareResult> => {
        setLoading(true);
        try {
            const { installed } = await InstagramShare.isInstalled();
            setInstagramInstalled(installed);
            if (!installed) return 'not-installed';
            if (!backgroundVideoURL) return 'error';

            const localVideo = await ensureLocalFile(backgroundVideoURL, 'video');
            const localSticker = stickerImageURL
                ? await ensureLocalFile(stickerImageURL, 'sticker')
                : undefined;

            await InstagramShare.shareToStory({
                backgroundVideoPath: localVideo,
                stickerImagePath: localSticker,
                contentURL,
                sourceApplication,
            });
            return 'shared';
        } catch (err) {
            console.error('Instagram share failed', err);
            return 'error';
        } finally {
            setLoading(false);
        }
    };

    return {
        instagramInstalled,
        loading,
        checkInstagram,
    };
};
